import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole, useRealmRoles } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import {
  ReportingGaugesDrawer,
  reportingEndpoints,
  useReportingGauges,
} from "@/features/reporting";
import { useJournalEntries } from "@/features/ledger/api/use-journal-entries";
import type { JournalEntryFilter } from "@/features/ledger/api/dto";
import {
  JournalEntryPostDialog,
  journalEntriesColumns,
  type JournalEntryRow,
} from "@/features/ledger/components";
import { useLedgerTranslation } from "@/features/ledger/i18n/use-ledger-translation";
import type { JournalEntryStatus } from "@/features/shared/api/enums";

const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const LEDGER_GAP_GAUGE = ["flashpay_ledger_balance_gap_minor"] as const;

/** Traduit les filtres de colonnes DataTableClar en `JournalEntryFilter` (§4.2). */
const toJournalEntryFilter = (
  filters: ColumnFiltersState
): JournalEntryFilter => {
  const filter: JournalEntryFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "productCode") filter.productCode = value;
    if (id === "sourceService") filter.sourceService = value;
    if (id === "sourceTransactionRef") filter.sourceTransactionRef = value;
    if (id === "status") filter.status = value as JournalEntryStatus;
  }
  return filter;
};

/**
 * Journal comptable paginé (`POST /JournalEntries/all`, guide §6.4) —
 * lecture authentifiée. La comptabilisation manuelle (`post`) est réservée
 * à flashpay-service / flashpay-superadmin / flashpay-admin, la
 * contre-passation à flashpay-superadmin / flashpay-admin (actions de
 * ligne masquées par rôle).
 */
export function JournalEntriesPage() {
  const { t } = useLedgerTranslation();
  const realmRoles = useRealmRoles();
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const ledgerGauges = useReportingGauges("ledger", canGauges);
  const [postOpen, setPostOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toJournalEntryFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useJournalEntries(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("ledger.journal.title")}
        </h1>
        <div className="flex flex-wrap items-center gap-3">
          <RequireRole
            roles={[
              FLASHPAY_ROLES.SERVICE,
              FLASHPAY_ROLES.SUPERADMIN,
              FLASHPAY_ROLES.ADMIN,
            ]}
          >
            <Button onClick={() => setPostOpen(true)}>
              <Plus className="mr-2 w-4 h-4" />
              {t("ledger.journal.post")}
            </Button>
          </RequireRole>
          {canGauges ? (
            <ReportingGaugesDrawer
              gauges={ledgerGauges.data ?? []}
              names={LEDGER_GAP_GAUGE}
              provenanceEndpoints={[reportingEndpoints.ledger.gauges]}
            />
          ) : null}
        </div>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((r): r is JournalEntryRow =>
          Boolean(r.id)
        )}
        columnsClar={journalEntriesColumns}
        title={t("ledger.journal.title")}
        exportFileName="journal-entries"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <JournalEntryPostDialog open={postOpen} onOpenChange={setPostOpen} />
    </section>
  );
}
